import React, { useState } from "react";

import { getAuthToken } from "../../lib/api/tokenStore";
import { showErrorToast, showSuccessToast } from "../../components/Toast";
import useMyReviews from "./useMyReviews";
import { useDeleteReview } from "./useReviewActions";
import ReviewCard from "./components/ReviewCard";
import ReviewFormDialog from "./components/ReviewFormDialog";
import DeleteReviewDialog from "./components/DeleteReviewDialog";
import { ReviewCardSkeleton, ReviewCardsSkeleton } from "./components/ReviewsSkeletons";
import { REVIEWS_COPY, REVIEWS_EMPTY_STATES, serverMessage } from "./reviewsCopy";

import "./styles.css";

const EmptyView = ({ state, onRetry }) => (
  <div className="rv-empty">
    <img src="/assets/icons/reviews.svg" alt="" aria-hidden="true" />

    <h2>{state.title}</h2>
    <p>{state.body}</p>

    {onRetry && (
      <div className="rv-empty-actions">
        <button type="button" className="rv-btn rv-btn-outline" onClick={onRetry}>
          Try again
        </button>
      </div>
    )}
  </div>
);

const MyReviewsRoute = () => {
  const enabled = Boolean(getAuthToken());
  const reviews = useMyReviews({ enabled });

  const [editing, setEditing] = useState(null);
  const [confirming, setConfirming] = useState(null);

  const { remove, deleting } = useDeleteReview();

  const onDelete = async () => {
    const result = await remove(confirming.id);

    setConfirming(null);

    if (result.status === "error") {
      showErrorToast(serverMessage(result.error, REVIEWS_COPY.deleteFailed));

      return;
    }

    showSuccessToast(REVIEWS_COPY.deleted.message, REVIEWS_COPY.deleted.title);
  };

  return (
    <div className="rv-screen">
      <header className="rv-pagehead">
        <h1 className="rv-pagetitle">My Reviews</h1>
      </header>

      <div className="rv-band">
        <div className="rv-section-header">
          <h2 className="rv-section-title">Your Feedback</h2>

          <div className="rv-section-aside">
            {!reviews.isLoading && reviews.items.length > 0 && (
              <p className="rv-results-count">{reviews.items.length}</p>
            )}

            <button
              type="button"
              className="rv-refresh"
              disabled={reviews.isFetching}
              onClick={reviews.refetch}
            >
              {reviews.isFetching ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>

        {reviews.isLoading ? (
          <ReviewCardsSkeleton cards={6} />
        ) : reviews.isError ? (
          <EmptyView state={REVIEWS_EMPTY_STATES.listError} onRetry={reviews.refetch} />
        ) : reviews.items.length ? (
          <ul className="rv-list">
            {reviews.items.map((review) => (
              <ReviewCard
                key={review.id}
                review={review}
                busy={deleting}
                onEdit={setEditing}
                onDelete={setConfirming}
              />
            ))}

            {reviews.isPaging &&
              Array.from({ length: 3 }).map((unused, index) => (
                <ReviewCardSkeleton key={`paging-${index}`} />
              ))}
          </ul>
        ) : (
          <EmptyView state={REVIEWS_EMPTY_STATES.noReviews} />
        )}

        {reviews.canLoadMore && (
          <div className="rv-more">
            <button type="button" className="rv-more-button" onClick={reviews.loadMore}>
              Load more
            </button>
          </div>
        )}
      </div>

      {editing && (
        <ReviewFormDialog
          review={editing}
          onClose={() => setEditing(null)}
          onSaved={reviews.refetch}
        />
      )}

      {confirming && (
        <DeleteReviewDialog
          busy={deleting}
          onConfirm={onDelete}
          onCancel={() => setConfirming(null)}
        />
      )}
    </div>
  );
};

export default MyReviewsRoute;
